import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Stack,
  InputAdornment,
  IconButton,
  LinearProgress,
  useTheme,
  alpha,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';
import {
  Security,
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Save,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';

function checkPasswordStrength(password) {
  if (!password) return { strength: 0, label: 'None', color: 'inherit' };

  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/\d/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;

  if (score >= 5) return { strength: 100, label: 'Strong', color: 'success' };
  if (score >= 3) return { strength: 60, label: 'Medium', color: 'warning' };
  return { strength: 30, label: 'Weak', color: 'error' };
}

export function LoginInformation() {
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const strength = checkPasswordStrength(newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword && newPassword !== repeatPassword) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'New passwords do not match.',
        confirmButtonColor: '#D4AF37'
      });
      return;
    }

    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Login information updated successfully.',
        confirmButtonColor: '#D4AF37'
      });
    }, 1500);
  };

  return (
    <Box sx={{ maxWidth: '600px', mx: 'auto' }}>
      <Typography variant="h4" fontWeight={800} textAlign="center" gutterBottom>
        Login Security
      </Typography>
      <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 6 }}>
        Keep your account secure by managing your email and periodically updating your password.
      </Typography>

      <form onSubmit={handleSubmit}>
        <Stack spacing={4}>
          <TextField
            fullWidth
            label="Account Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            InputProps={{
              startAdornment: <InputAdornment position="start"><Email color="primary" /></InputAdornment>,
            }}
          />

          <TextField
            fullWidth
            label="Current Password"
            type={showCurrentPassword ? "text" : "password"}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            placeholder="Required to authorize changes"
            helperText="Confirm your identity to update your details"
            InputProps={{
              startAdornment: <InputAdornment position="start"><Lock color="primary" /></InputAdornment>,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)}>
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ textAlign: 'right' }}>
            <Typography 
              component={Link} 
              to="/db-shiksha/kaas/reset-password" 
              sx={{ color: 'primary.main', fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Forgot your password?
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }}>
            <Chip label="CHANGE PASSWORD" size="small" sx={{ fontWeight: 800 }} />
          </Divider>

          <Alert severity="info" icon={<Security />} sx={{ borderRadius: '12px' }}>
            Leave new password fields blank if you only want to update your email.
          </Alert>

          <Box>
            <TextField
              fullWidth
              label="New Password"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Lock color="primary" /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNewPassword(!showNewPassword)}>
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {newPassword && (
              <Box sx={{ mt: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" fontWeight={700} color={`${strength.color}.main`}>
                    Strength: {strength.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">12+ characters recommended</Typography>
                </Stack>
                <LinearProgress 
                  variant="determinate" 
                  value={strength.strength} 
                  color={strength.color}
                  sx={{ height: 6, borderRadius: 3, bgcolor: alpha(theme.palette.divider, 0.1) }}
                />
              </Box>
            )}
          </Box>

          <TextField
            fullWidth
            label="Repeat New Password"
            type={showRepeatPassword ? "text" : "password"}
            value={repeatPassword}
            onChange={(e) => setRepeatPassword(e.target.value)}
            error={newPassword && repeatPassword && newPassword !== repeatPassword}
            helperText={newPassword && repeatPassword && newPassword !== repeatPassword ? "Passwords do not match" : ""}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Lock color="primary" /></InputAdornment>,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowRepeatPassword(!showRepeatPassword)}>
                    {showRepeatPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ pt: 2, textAlign: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
              sx={{ px: 8, py: 1.5, borderRadius: '50px', fontWeight: 800, boxShadow: '0 8px 25px rgba(212, 175, 55, 0.3)' }}
            >
              Update Information
            </Button>
          </Box>
        </Stack>
      </form>
    </Box>
  );
}
